import { useState, useEffect } from 'react';
import { getSensorHistory, type HistoryReading } from '../services/sensors';

/**
 * Histórico REAL de un sensor, desde la base.
 *
 * Existe para reemplazar a los generadores sintéticos del frontend
 * (getDemoBatteryHistory, getDemoParamHistory). Aquéllos buscaban el imei en
 * una lista de sensores demo y, al no encontrarlo —que es SIEMPRE el caso de
 * un sensor real—, dibujaban una curva alrededor de un valor fijo con
 * `Math.random()`. La tarjeta mostraba el dato bueno y la gráfica de abajo
 * mostraba otra cosa.
 *
 * Regla de este hook, igual que en el resto de la plataforma: cuando no hay
 * datos, se devuelve una lista vacía y quien lo consuma debe DECIRLO. Nunca
 * rellenar: una línea inventada se lee como una tendencia real.
 */
export interface UseSensorHistory {
  readings: HistoryReading[];
  loading: boolean;
  /** true si la petición falló; distinto de "no hay datos todavía". */
  error: boolean;
  /** 'raw' hasta 48 h, 'hourly' para ventanas más largas. */
  source: 'raw' | 'hourly' | null;
}

/**
 * Ventana a consultar: por horas hacia atrás, o un rango de calendario
 * exacto. Se pasan como strings ISO para que sirvan de dependencia estable
 * del efecto — dos objetos Date con el mismo instante no son iguales entre
 * renders y dispararían una petición nueva en cada uno.
 */
export type VentanaHistorico =
  | { hours: number }
  | { from: string; to: string };

export function useSensorHistory(
  imei: string | null | undefined,
  ventana: VentanaHistorico,
  depth?: number,
): UseSensorHistory {
  const [readings, setReadings] = useState<HistoryReading[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [source, setSource] = useState<'raw' | 'hourly' | null>(null);

  useEffect(() => {
    if (!imei) {
      setReadings([]);
      setSource(null);
      return;
    }

    // `cancelado` evita que una respuesta lenta de un rango anterior pise a la
    // del rango que el usuario acaba de elegir: sin esto, cambiar rápido entre
    // 24h y 7d puede dejar la gráfica mostrando el rango equivocado.
    let cancelado = false;
    setLoading(true);
    setError(false);

    getSensorHistory(imei, { ...ventana, depth, limit: 10000 })
      .then(res => {
        if (cancelado) return;
        // El backend devuelve lo más reciente primero; las gráficas necesitan
        // orden cronológico.
        const ordenadas = [...res.readings].sort((a, b) =>
          (a.receivedAt ?? '').localeCompare(b.receivedAt ?? ''),
        );
        setReadings(ordenadas);
        setSource(res.source);
      })
      .catch(() => {
        if (cancelado) return;
        // Vacío + error: quien consuma debe distinguir "falló la consulta" de
        // "este sensor todavía no tiene historia". No son lo mismo para el
        // usuario y no se deben mostrar igual.
        setReadings([]);
        setSource(null);
        setError(true);
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });

    return () => { cancelado = true; };
    // Dependencias desestructuradas a propósito: `ventana` es un objeto
    // literal que cambia de identidad en cada render, y usarlo directo
    // dispararía una petición infinita.
  }, [imei, depth, 'hours' in ventana ? ventana.hours : null,
      'from' in ventana ? ventana.from : null, 'to' in ventana ? ventana.to : null]);

  return { readings, loading, error, source };
}

/**
 * Aplana el histórico a una serie de una sola métrica.
 *
 * Cada lectura trae un payload con varias métricas y una profundidad. Para
 * graficar hace falta elegir una métrica y, opcionalmente, una profundidad;
 * si hay varias profundidades en la ventana, se promedian por instante.
 *
 * Descarta los puntos donde la métrica no viene, en vez de meterlos como 0:
 * un hueco en la serie es honesto, un cero es una medición que nadie tomó.
 */
export function toSeries(
  readings: HistoryReading[],
  metricKey: string,
): Array<{ ts: string; value: number }> {
  const porInstante = new Map<string, { suma: number; n: number }>();

  for (const r of readings) {
    const v = r.payload?.[metricKey];
    if (typeof v !== 'number' || !Number.isFinite(v)) continue;
    const ts = r.receivedAt;
    if (!ts) continue;
    const acc = porInstante.get(ts) ?? { suma: 0, n: 0 };
    acc.suma += v;
    acc.n += 1;
    porInstante.set(ts, acc);
  }

  return [...porInstante.entries()]
    .map(([ts, { suma, n }]) => ({ ts, value: suma / n }))
    .sort((a, b) => a.ts.localeCompare(b.ts));
}
